import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { kioskApi } from '../../api/kiosk.js';
import { ApiError } from '../../api/client.js';
import { Card } from '../../components/ui/Card.jsx';
import { FullScreenLoader } from '../../components/ui/FullScreenLoader.jsx';

import { KioskAuthPanel } from './KioskAuthPanel.jsx';
import { ConsentBanner } from './ConsentBanner.jsx';
import { KioskChat } from './KioskChat.jsx';

const STORAGE_KEY = 'orme:kiosk:session';

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.customerSessionId) return null;
    if (parsed.expiresAt && new Date(parsed.expiresAt) < new Date()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveSession(session) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(session)); } catch (_e) { /* ignore */ }
}

export function KioskHomePage() {
  const { status, role, isAuthenticated } = useAuth();
  const [session, setSession] = useState(() => loadSession());
  const [checking, setChecking] = useState(Boolean(loadSession()));

  // Validate persisted session against the server on mount: the
  // browser may have outlived the server-side TTL.
  useEffect(() => {
    let cancelled = false;
    async function check() {
      const persisted = loadSession();
      if (!persisted || !isAuthenticated) { setChecking(false); return; }
      try {
        const fresh = await kioskApi.status(persisted.customerSessionId);
        if (cancelled) return;
        setSession(fresh);
        saveSession(fresh);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
          setSession(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (status === 'loading') return <FullScreenLoader />;

  if (!isAuthenticated || role !== 'kiosk') {
    if (role && role !== 'kiosk' && isAuthenticated) {
      return (
        <div className="mx-auto max-w-md px-6 py-12">
          <Card>
            <h1 className="text-lg font-semibold text-ink">Postazione non kiosk</h1>
            <p className="mt-2 text-sm text-ink-muted">
              L'account corrente ({role}) non è una postazione kiosk. Esci e accedi con le credenziali della postazione.
            </p>
          </Card>
        </div>
      );
    }
    return <KioskAuthPanel />;
  }

  if (checking) return <FullScreenLoader />;

  if (!session) {
    return (
      <ConsentBanner
        onGranted={(s) => { saveSession(s); setSession(s); }}
      />
    );
  }

  return (
    <KioskChat
      session={session}
      onSessionEnded={() => setSession(null)}
    />
  );
}
